<template>
  <error-boundary error-key="quota">
    <page-content-placeholder
      v-if="activeTariff?.remoteInteraction && activeAccount?.status === ContractStatus.OnApproval"
      max-width="640px"
      :img-src="TLPlaceholder"
    >
      <div class="caption">
        Квота номеров будет загружена после проверки вашего аккаунта модератором и&nbsp;изменения его&nbsp;статуса на
        <span class="text-green">«В&nbsp;работе»</span>
      </div>
    </page-content-placeholder>

    <page-content-placeholder
      v-else-if="
        activeAccount?.status === ContractStatus.InProgress &&
        activeQuota &&
        !activeQuota.roomQuotas.length &&
        activeTariff?.remoteInteraction
      "
      max-width="640px"
      :img-src="TLPlaceholder"
    >
      <div class="caption">
        Квоты будут загружены после успешной синхронизации с&nbsp;менеджером каналов в течение нескольких минут
      </div>
    </page-content-placeholder>

    <div v-else-if="activeQuota?.roomQuotas.length" class="d-flex flex-column flex-grow-1">
      <div class="d-flex">
        <e-btn
          v-if="isOperationVisible(activeQuota?.operations.addRooms)"
          variant="primary"
          :disabled="isOperationDisabled(activeQuota?.operations.addRooms)"
          :loading="isEditQuotaInitialValuesStartLoading"
          @click="handleInitialValuesSet"
        >
          Установить начальные значения
        </e-btn>
        <e-btn
          v-if="$vuetify.display.mdAndUp && isOperationVisible(activeQuota?.operations.updateReserves)"
          class="ml-auto"
          variant="secondary"
          :disabled="isOperationDisabled(activeQuota?.operations.updateReserves)"
          :loading="isEditQuotaReservesStartLoading"
          @click="handleGroupOperation"
        >
          Групповая операция
        </e-btn>
      </div>
      <chess
        ref="chessRef"
        class="my-6"
        :period="chessPeriod"
        :items="chessItems"
        :is-edit="isOperationEnabled(activeQuota?.operations.updateReserves)"
        :chess-dates-available-from="getIsoDateWithUtcOffset(new Date(), timeZoneOffsetMin)"
        :loading="isLoading"
        :loader-visible="isEditQuotaReservesStartLoading"
        @select="handleChessSelect"
      />
      <e-btn
        v-if="$vuetify.display.smAndDown && isOperationVisible(activeQuota?.operations.updateReserves)"
        class="mt-auto ml-auto"
        variant="secondary"
        :disabled="isOperationDisabled(activeQuota?.operations.updateReserves)"
        :loading="isEditQuotaReservesStartLoading"
        @click="handleGroupOperation"
      >
        Групповая операция
      </e-btn>
    </div>

    <page-content-placeholder v-else-if="activeQuota" :img-src="Placeholder" text="В квоте пока нет номеров">
      <e-btn
        v-if="isOperationVisible(activeQuota?.operations.addRooms)"
        class="mt-6"
        variant="primary"
        :disabled="isOperationDisabled(activeQuota?.operations.addRooms)"
        :loading="isEditQuotaInitialValuesStartLoading"
        @click="handleInitialValuesSet"
      >
        Установить начальные значения
      </e-btn>
    </page-content-placeholder>

    <quota-initial-values-dialog
      v-model="isInitialValuesDialogOpen"
      :room-categories="roomCategories"
      :tariff-id="tariffId"
    />
    <quota-reserves-dialog
      v-model="isReservesDialogOpen"
      :edited-cells="selectedChessCells"
      :tariff-id="tariffId"
      @close="handleReservesDialogClose"
    />
  </error-boundary>
</template>

<script setup lang="ts">
import dayjs from 'dayjs';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useDisplay } from 'vuetify';
import type { ChessDateCellType, ChessTableRowCellsType } from '@/core/components/interface/Chess/Chess.type';
import type { QuotaInitialValuesRoomCategoryType } from '@/parts/tariffs/store/quota';
import Placeholder from '@/assets/img/pages/room-categories/no-room-categories.svg';
import TLPlaceholder from '@/assets/img/pages/tariffs/TL-grid-placeholder.svg';
import PageContentPlaceholder from '@/core/components/common/PageContentPlaceholder.vue';
import Chess from '@/core/components/interface/Chess/index.vue';
import { EBtn } from '@/core/components/ui';
import { useFetch } from '@/core/composables/useFetch';
import useToggle from '@/core/composables/useToggle';
import { getIsoDateWithUtcOffset } from '@/core/utils/date';
import { isOperationDisabled, isOperationEnabled, isOperationVisible } from '@/core/utils/permissions';
import { ContractStatus } from '@/graphql/generated';
import ErrorBoundary from '@/parts/errors/components/ErrorBoundary.vue';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';
import QuotaInitialValuesDialog from '@/parts/tariffs/components/Quota/QuotaInitialValuesDialog.vue';
import QuotaReservesDialog from '@/parts/tariffs/components/Quota/QuotaReservesDialog.vue';
import { useQuotaStore } from '@/parts/tariffs/store/quota';
import { useTariffsStore } from '@/parts/tariffs/store/tariffs';

const $route = useRoute();
const { activeAccount } = useAccommodationObjectsStore();
const { activeTariff } = useTariffsStore();
const display = useDisplay();
const [isInitialValuesDialogOpen, setIsInitialValuesDialogOpen] = useToggle();
const [isReservesDialogOpen, setIsReservesDialogOpen] = useToggle();
const {
  activeQuota,
  getActiveQuota,
  setActiveQuota,
  editQuotaInitialValuesStart,
  isEditQuotaInitialValuesStartLoading,
  editQuotaReservesStart,
  isEditQuotaReservesStartLoading,
} = useQuotaStore();
const { timeZoneOffsetMin } = useAccommodationObjectsStore();

const chessRef = ref();
const selectedChessCells = ref<ChessDateCellType[]>([]);
const roomCategories = ref<QuotaInitialValuesRoomCategoryType[]>([]);

const tariffId = computed(() => String($route.params.t_id));

const chessPeriod = computed(() => {
  const firstQuota = activeQuota.value?.roomQuotas[0]?.quotas.at(0);
  const lastQuota = activeQuota.value?.roomQuotas[0]?.quotas.at(-1);

  return { from: firstQuota?.period.from, to: lastQuota?.period.to };
});

const chessItems = computed(() => {
  if (!activeQuota.value) return [];

  return [
    {
      tables: [
        {
          rows: activeQuota.value?.roomQuotas.map((item) => ({
            fixed: {
              title: `<div class="text-ellipsis">${item.room.name[0].value}&nbsp;&nbsp;${item.room.type.name}</div>`,
              text: '<div class="mt-1">Бронирования <br/> Квота</div> ',
            },
            cells: item.quotas.reduce<ChessTableRowCellsType>((result, quota) => {
              let periodContent = `<div class="text-ordinary">${quota.ordered}</div>`;

              if (quota.isClosed) {
                periodContent += 'Закрыта';
              } else if (quota.reserve) {
                if (quota.reserve.byRequest) {
                  periodContent += 'Запрос';
                } else {
                  periodContent += `${quota.reserve.free} / ${quota.reserve.reserved}`;
                }
              }

              let periodDate = quota.period.from;

              while (periodDate <= quota.period.to) {
                result[periodDate] = { date: periodDate, content: periodContent, id: item.room.cid };
                periodDate = dayjs(periodDate).add(1, 'day').format('YYYY-MM-DD');
              }

              return result;
            }, {}),
          })),
          rowCellWidth: 76,
          cellHeight: display.xs.value ? 52 : 66,
        },
      ],
    },
  ];
});

const handleInitialValuesSet = async () => {
  const result = await editQuotaInitialValuesStart(tariffId.value);

  if (!result.errors && result.data?.editQuotaAddRooms.roomQuotas) {
    roomCategories.value = result.data.editQuotaAddRooms.roomQuotas;
    setIsInitialValuesDialogOpen(true);
  }
};

const handleGroupOperation = async () => {
  const result = await editQuotaReservesStart(tariffId.value);

  if (!result.errors && result.data?.editQuotaReservesUpdate.Quota) {
    setActiveQuota(result.data.editQuotaReservesUpdate.Quota);
    setIsReservesDialogOpen(true);
  }
};

const handleChessSelect = async (cells: ChessDateCellType[]) => {
  selectedChessCells.value = cells;

  if (!cells.length) return;

  const result = await editQuotaReservesStart(tariffId.value);

  if (!result.errors && result.data?.editQuotaReservesUpdate.Quota) {
    setActiveQuota(result.data.editQuotaReservesUpdate.Quota);
    setIsReservesDialogOpen(true);
  } else {
    chessRef.value?.unselectAllCells();
  }
};

const handleReservesDialogClose = () => {
  chessRef.value?.unselectAllCells();
};

const { isLoading } = useFetch(async () => {
  await getActiveQuota(tariffId.value);
});
</script>
